import React, { useState, useEffect } from 'react';
import axios from "axios";
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import NavTab from '../components/NavTab/NavTab';
import Accordion from '../components/NavTab/Accordion';
import { useAuth } from '../context/AuthProvider';
import AccountInformation from '../components/Account/AccountInformation';
import SubscriptionPlan from '../components/Account/SubscriptionPlan';
import IdentityVerification from '../components/Account/IdentityVerification';
import PasswordSection from '../components/Account/PasswordSection';
import SubscriptionInvoices from '../components/Account/SubscriptionInvoices';
import DeactivateAccountSection from '../components/Account/DeactivateAccountSection';
import TwoFactorSection from '../components/Account/TwoFactorSection';
import Form from '../components/Form/Form';
import Button from '../components/Form/Button';
import Modal from '../components/Modal/Modal';
import ButtonContainer from '../components/Container/ButtonContainer';
import { toast } from 'react-toastify';
import '../components/Modal/CreateProductModal.scss';
import './Account.scss';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import {
  faUser,
  faStar,
  faFileInvoiceDollar,
  faLock,
  faShieldHalved,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../api';

const SAVE_BUTTON_TABS = new Set(['accountInformation', 'accountPassword']);

// Each tab gets its own bookmarkable/refreshable URL, e.g. /account/info.
const TAB_SLUGS = {
  accountInformation: 'info',
  subscriptionPlan: 'subscription',
  subscriptionInvoices: 'invoices',
  accountPassword: 'password',
};
const SLUG_TO_TAB = Object.fromEntries(Object.entries(TAB_SLUGS).map(([tab, slug]) => [slug, tab]));

const Account = () => {
  const { t } = useTranslation();
  const { tab } = useParams();
  const navigate = useNavigate();
  const [formLoading, setFormLoading] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const activeTab = SLUG_TO_TAB[tab] || 'accountInformation';
  const setActiveTab = (id) => navigate(`/account/${TAB_SLUGS[id] || ''}`, { replace: true });
  const { setTitle } = usePageTitle();
  const { login, user } = useAuth();

  useEffect(() => {
    setTitle(`${user.firstName} ${user.lastName}`);
    document.title = `${user.firstName} ${user.lastName}`;
  }, [setTitle]);

  useBreadcrumbSetup([
    { label: t('account.breadcrumb') }
  ]);

  // Saving is staged behind a password-confirmation modal (see below) — this only
  // fires once the user has re-entered their current password there.
  const performSave = (data) => {
    setFormLoading(true);
    axios.put(`${API_BASE_URL}/api/auth/me/update`, data, { withCredentials: true })
      .then(() => {
        toast.success(t('account.toast.saved'));
        setPendingData(null);
        login();
      })
      .catch((error) => {
        console.error('Error saving account:', error);
        toast.error(error?.response?.data?.message || t('account.toast.save_error'));
      })
      .finally(() => setFormLoading(false));
  };

  const handleSubmit = (data) => {
    setPendingData(data);
  };

  const handleVerifyConfirm = (verifyData) => {
    performSave({ ...pendingData, currentPassword: verifyData.currentPassword });
  };

  const menuItems = [
    {
      id: 'accountInformation',
      icon: faUser,
      label: t('Account Information'),
      component: (
        <Accordion id="accountInformation" title={t('Account Information')} icon={faUser}>
          <AccountInformation user={user} />
        </Accordion>
      ),
    },
    {
      id: 'subscriptionPlan',
      icon: faStar,
      label: t('Subscription Plan'),
      component: (
        <Accordion id="subscriptionPlan" title={t('Subscription Plan')} icon={faStar}>
          <SubscriptionPlan user={user} />
        </Accordion>
      ),
    },
    {
      id: 'subscriptionInvoices',
      icon: faFileInvoiceDollar,
      label: t('Invoices'),
      component: (
        <Accordion id="subscriptionInvoices" title={t('account.section.invoices')} icon={faFileInvoiceDollar}>
          <SubscriptionInvoices />
        </Accordion>
      ),
    },
    {
      id: 'accountPassword',
      icon: faLock,
      label: t('Update Password'),
      component: (
        <Accordion id="accountPassword" title={t('Update Password')} icon={faLock}>
          <PasswordSection user={user} />
          <TwoFactorSection user={user} />
          <DeactivateAccountSection user={user} />
        </Accordion>
      ),
    },
  ];

  const buttons = [
    { label: t('Save Account'), icon: faCheckCircle, className: 'sub-confirm-btn', style: {}, form: 'updateAccountForm', isLoading: formLoading },
  ];

  return (
    <div className="page-content account-page">
      <Form id="updateAccountForm" onSubmit={handleSubmit} defaultValues={{ ...user, pharmacyId: 1 }}>
        <NavTab menuItems={menuItems} activeTab={activeTab} onTabChange={setActiveTab} />
      </Form>

      {SAVE_BUTTON_TABS.has(activeTab) && <ButtonContainer buttons={buttons} />}

      <Modal
        id="verifyIdentityModal"
        title={t('Identity Verification')}
        show={!!pendingData}
        onClose={() => setPendingData(null)}
        icon={faShieldHalved}
        iconBg="#fef2f2"
        iconColor="#dc2626"
        footer={
          <Button
            isLoading={formLoading}
            loadingMessage={t('account.verify.loading')}
            className="btn btn-primary w-100"
            type="submit"
            form="verifyIdentityForm"
          >
            {t('account.verify.confirm_button')}
          </Button>
        }
      >
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
          {t('account.verify.description')}
        </p>
        <Form id="verifyIdentityForm" onSubmit={handleVerifyConfirm}>
          <IdentityVerification />
        </Form>
      </Modal>
    </div>
  );
};

export default Account;
